import React, { useEffect, useState } from 'react';
import { getLocations } from '../../services/inventoryCorrections.service';
import { getSalesOverview } from '../../services/salesOverview.service';
import { useResponsive } from '../../hook/useResponsive';
import { FilterField, IconCalendar } from '../../components/FilterField';

type Kpi = {
  value: number;
  prev: number;
  deltaPct: number;
  count?: number;
};

type DailyPoint = {
  date: string;
  revenue: number;
  refunds: number;
  discounts: number;
  serviceCharge: number;
  tip: number;
  txnCount: number;
};

type Tender = {
  deviceTenderId: string | null;
  tenderName: string;
  total: number;
  sharePct: number;
};

type SalesOverviewData = {
  period: {
    from: string;
    to: string;
    tz: string;
    compareFrom: string;
    compareTo: string;
  };
  kpis: {
    revenue: Kpi;
    netSales: Kpi;
    refunds: Kpi;
    discounts: Kpi;
    serviceCharge: Kpi;
    tip: Kpi;
    avgSpendPerTxn: Kpi;
    txnCount: Kpi;
  };
  series: {
    daily: DailyPoint[];
  };
  tenders: Tender[];
};

type PresetId = 'thisWeek' | 'lastWeek' | 'mtd' | 'custom';

const emptyData: SalesOverviewData = {
  period: { from: '', to: '', tz: 'UTC', compareFrom: '', compareTo: '' },
  kpis: {
    revenue: { value: 0, prev: 0, deltaPct: 0 },
    netSales: { value: 0, prev: 0, deltaPct: 0 },
    refunds: { value: 0, prev: 0, deltaPct: 0, count: 0 },
    discounts: { value: 0, prev: 0, deltaPct: 0 },
    serviceCharge: { value: 0, prev: 0, deltaPct: 0 },
    tip: { value: 0, prev: 0, deltaPct: 0 },
    avgSpendPerTxn: { value: 0, prev: 0, deltaPct: 0 },
    txnCount: { value: 0, prev: 0, deltaPct: 0 },
  },
  series: { daily: [] },
  tenders: [],
};

const TEAL = '#0F7679';
const TEAL_DARK = '#05292A';
const BG = '#F4F8F9';
const BORDER = '#E5ECEC';
const MUTED = '#6B7787';
const POSITIVE_BG = '#E6F6EE';
const POSITIVE_TEXT = '#0E8B53';
const NEGATIVE_BG = '#FDECEC';
const NEGATIVE_TEXT = '#C53030';
const CARD_SHADOW = '0 1px 2px rgba(5,41,42,0.04), 0 8px 24px rgba(5,41,42,0.05)';
const TENDER_COLORS = ['#0F7679', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#0EA5E9', '#64748B'];

const toYyyyMmDd = (date: Date) => date.toISOString().slice(0, 10);
const startOfWeekUtc = () => {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysSinceMonday = (start.getUTCDay() + 6) % 7;
  start.setUTCDate(start.getUTCDate() - daysSinceMonday);
  return toYyyyMmDd(start);
};
const startOfMonthUtc = () => {
  const now = new Date();
  return toYyyyMmDd(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)));
};
const addDays = (value: string, days: number) => {
  const date = new Date(`${value}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return toYyyyMmDd(date);
};

/**
 * Figures carry no currency symbol: the platform has no per-company currency
 * (nothing in either database records one), so a hard-coded "€" on every
 * value is a claim we cannot back. The unit is stated once under the page
 * title instead — the same convention the exported statements use.
 */
const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-DE').format(Number(value || 0));
// Month names follow the dashboard language ('al' renders Albanian months).
const dateLocale = () =>
  localStorage.getItem('language') === 'al' ? 'sq-AL' : 'en-GB';
const formatShortDate = (iso: string) => {
  const date = new Date(`${iso}T00:00:00.000Z`);
  return date.toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', timeZone: 'UTC' });
};
const formatRange = (from: string, to: string) => {
  if (!from || !to) return '';
  return `${formatShortDate(from)} → ${formatShortDate(to)}`;
};

const SalesOverview = () => {
  const { isMobile } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const today = toYyyyMmDd(new Date());

  const [preset, setPreset] = useState<PresetId>('thisWeek');
  const [from, setFrom] = useState(startOfWeekUtc());
  const [to, setTo] = useState(today);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState<any[]>([]);
  const [data, setData] = useState<SalesOverviewData>(emptyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, []);

  useEffect(() => {
    const loadLocations = async () => {
      if (!companyId || !userId) return;
      try {
        const response: any = await getLocations(userId, companyId, 0, 0, '');
        setLocations(response?.data?.data ?? []);
      } catch (err) {
        console.error('Error loading locations:', err);
      }
    };

    loadLocations();
  }, [companyId, userId]);

  useEffect(() => {
    if (preset === 'custom') return;
    if (preset === 'thisWeek') {
      setFrom(startOfWeekUtc());
      setTo(today);
      return;
    }
    if (preset === 'lastWeek') {
      const thisWeekStart = startOfWeekUtc();
      setFrom(addDays(thisWeekStart, -7));
      setTo(addDays(thisWeekStart, -1));
      return;
    }
    setFrom(startOfMonthUtc());
    setTo(today);
  }, [preset, today]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const response: any = await getSalesOverview({
        from,
        to,
        locationId,
        tz: 'UTC',
      });
      setData(response?.data?.data ?? emptyData);
    } catch (err) {
      console.error('Error loading sales overview:', err);
      setData(emptyData);
      setError(language?.['Unable to load Sales Overview'] ?? 'Unable to load Sales Overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [from, to, locationId]);

  const totalTender = data.tenders.reduce((sum, tender) => sum + Number(tender.total || 0), 0);
  const presetLabel = (id: PresetId) =>
    id === 'thisWeek'
      ? language?.['This week'] ?? 'This week'
      : id === 'lastWeek'
        ? language?.['Last week'] ?? 'Last week'
        : id === 'mtd'
          ? language?.['MTD'] ?? 'MTD'
          : language?.['Custom'] ?? 'Custom';

  const kpiCards: Array<{
    key: string;
    title: string;
    kpi: Kpi;
    formatter: (value: number) => string;
    icon: React.ReactNode;
    accent: string;
    sub?: string;
    invertDelta?: boolean;
    series?: number[];
  }> = [
    {
      key: 'netSales',
      title: language?.['Net Sales'] ?? 'Net Sales',
      kpi: data.kpis.netSales,
      formatter: formatMoney,
      icon: <IconReceipt />,
      accent: '#3B82F6',
      series: data.series.daily.map((p) => p.revenue),
    },
    {
      key: 'discounts',
      title: language?.['Discounts'] ?? 'Discounts',
      kpi: data.kpis.discounts,
      formatter: formatMoney,
      icon: <IconTag />,
      accent: '#F59E0B',
      invertDelta: true,
      series: data.series.daily.map((p) => p.discounts),
    },
    {
      key: 'refunds',
      title: language?.['Refunds'] ?? 'Refunds',
      kpi: data.kpis.refunds,
      formatter: formatMoney,
      icon: <IconRefund />,
      accent: '#EF4444',
      invertDelta: true,
      sub: `${formatNumber(data.kpis.refunds.count ?? 0)} ${language?.['refunds'] ?? 'refunds'}`,
      series: data.series.daily.map((p) => p.refunds),
    },
    {
      key: 'serviceCharge',
      title: language?.['Service charge'] ?? 'Service charge',
      kpi: data.kpis.serviceCharge,
      formatter: formatMoney,
      icon: <IconSparkle />,
      accent: '#8B5CF6',
      series: data.series.daily.map((p) => p.serviceCharge),
    },
    {
      key: 'tip',
      title: language?.['Tip'] ?? 'Tip',
      // Guards keep the page up if the API is deployed after the web.
      kpi: data.kpis.tip ?? emptyData.kpis.tip,
      formatter: formatMoney,
      icon: <IconCoin />,
      accent: '#D97706',
      series: data.series.daily.map((p) => p.tip ?? 0),
    },
    {
      key: 'avgSpendPerTxn',
      title: language?.['Avg spend / txn'] ?? 'Avg spend / txn',
      kpi: data.kpis.avgSpendPerTxn,
      formatter: formatMoney,
      icon: <IconBag />,
      accent: TEAL,
    },
    {
      key: 'txnCount',
      title: language?.['Transactions'] ?? 'Transactions',
      kpi: data.kpis.txnCount,
      formatter: formatNumber,
      icon: <IconActivity />,
      accent: '#0EA5E9',
      series: data.series.daily.map((p) => p.txnCount),
    },
  ];

  return (
    <div
      className="h-full overflow-auto font-inter"
      style={{
        color: TEAL_DARK,
        backgroundColor: BG,
        backgroundImage:
          'radial-gradient(1200px 400px at -10% -20%, rgba(15,118,121,0.08), transparent 60%), radial-gradient(900px 320px at 110% -10%, rgba(59,130,246,0.06), transparent 60%)',
        padding: isMobile ? '16px' : '24px',
      }}
    >
      <Header
        language={language}
        loading={loading}
        period={{ from: data.period.from || from, to: data.period.to || to }}
        comparePeriod={{ from: data.period.compareFrom, to: data.period.compareTo }}
        preset={preset}
        setPreset={setPreset}
        from={from}
        to={to}
        setFrom={(value) => {
          setPreset('custom');
          setFrom(value);
        }}
        setTo={(value) => {
          setPreset('custom');
          setTo(value);
        }}
        locationId={locationId}
        setLocationId={setLocationId}
        locations={locations}
        presetLabel={presetLabel}
        onRefresh={loadData}
      />

      {error && (
        <div
          className="mt-4 flex items-center gap-2 rounded-xl px-4 py-3"
          style={{ backgroundColor: NEGATIVE_BG, color: NEGATIVE_TEXT, fontSize: '13px' }}
          role="alert"
        >
          <IconAlert />
          {error}
        </div>
      )}

      <RevenueHero
        language={language}
        loading={loading}
        revenue={data.kpis.revenue}
        period={{ from: data.period.from || from, to: data.period.to || to }}
        compareLabel={
          data.period.compareFrom && data.period.compareTo
            ? formatRange(data.period.compareFrom, data.period.compareTo)
            : ''
        }
        daily={data.series.daily}
      />

      <div
        className="grid mt-5"
        style={{ gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
      >
        {kpiCards.map((card) => (
          <KpiCard
            key={card.key}
            title={card.title}
            value={card.formatter(card.kpi.value)}
            previous={card.formatter(card.kpi.prev)}
            previousLabel={language?.['vs prev'] ?? 'vs prev'}
            deltaPct={card.kpi.deltaPct}
            invertDelta={card.invertDelta}
            sub={card.sub}
            icon={card.icon}
            accent={card.accent}
            series={card.series}
            loading={loading}
          />
        ))}
      </div>

      <div
        className="grid mt-5"
        style={{
          gap: '16px',
          gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 2fr) minmax(280px, 1fr)',
        }}
      >
        <DailyTrendChart
          language={language}
          loading={loading}
          daily={data.series.daily}
          isMobile={isMobile}
        />
        <TenderSplit
          language={language}
          loading={loading}
          tenders={data.tenders}
          totalTender={totalTender}
        />
      </div>
    </div>
  );
};

const Header = ({
  language,
  loading,
  period,
  comparePeriod,
  preset,
  setPreset,
  from,
  to,
  setFrom,
  setTo,
  locationId,
  setLocationId,
  locations,
  presetLabel,
  onRefresh,
}: {
  language: any;
  loading: boolean;
  period: { from: string; to: string };
  comparePeriod: { from?: string; to?: string };
  preset: PresetId;
  setPreset: (id: PresetId) => void;
  from: string;
  to: string;
  setFrom: (value: string) => void;
  setTo: (value: string) => void;
  locationId: string;
  setLocationId: (value: string) => void;
  locations: any[];
  presetLabel: (id: PresetId) => string;
  onRefresh: () => void;
}) => {
  const presets: PresetId[] = ['thisWeek', 'lastWeek', 'mtd', 'custom'];
  const { isMobile } = useResponsive();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div
            className="inline-flex items-center rounded-full"
            style={{
              backgroundColor: 'rgba(15,118,121,0.08)',
              color: TEAL,
              fontSize: '12px',
              fontWeight: 600,
              gap: '8px',
              padding: '6px 14px',
            }}
          >
            <span style={{ width: '6px', height: '6px', borderRadius: '999px', backgroundColor: TEAL, display: 'inline-block' }} />
            {language?.['Reporting'] ?? 'Reporting'}
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, marginTop: '10px', letterSpacing: '-0.02em' }}>
            {language?.['Sales Overview'] ?? 'Sales Overview'}
          </h1>
          <p style={{ fontSize: '14px', color: MUTED, marginTop: '6px' }}>
            {formatRange(period.from, period.to)}
            {comparePeriod.from && comparePeriod.to && (
              <span style={{ color: '#94A3B8' }}>
                {'  ·  '}
                {language?.['Compared with'] ?? 'Compared with'} {formatRange(comparePeriod.from, comparePeriod.to)}
              </span>
            )}
            <span style={{ color: '#94A3B8' }}>
              {'  ·  '}
              {language?.['All figures in EUR'] ?? 'All figures in EUR'}
            </span>
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2 transition"
          style={{
            backgroundColor: TEAL_DARK,
            color: '#fff',
            fontSize: '13px',
            fontWeight: 600,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'wait' : 'pointer',
            boxShadow: '0 6px 16px rgba(5,41,42,0.18)',
          }}
        >
          <IconRefresh spinning={loading} />
          {loading ? (language?.['Refreshing'] ?? 'Refreshing') : (language?.['Refresh'] ?? 'Refresh')}
        </button>
      </div>

      <div
        className="flex flex-wrap items-center rounded-2xl"
        style={{
          backgroundColor: '#fff',
          border: `1px solid ${BORDER}`,
          padding: isMobile ? '12px' : '10px',
          boxShadow: CARD_SHADOW,
          gap: isMobile ? '12px' : '12px',
        }}
      >
        <div
          className={`${isMobile ? 'flex w-full' : 'inline-flex'} items-center rounded-xl`}
          style={{ backgroundColor: BG, padding: '4px' }}
        >
          {presets.map((id) => {
            const active = preset === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setPreset(id)}
                className="rounded-lg transition"
                style={{
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  backgroundColor: active ? '#fff' : 'transparent',
                  color: active ? TEAL_DARK : MUTED,
                  boxShadow: active ? '0 1px 3px rgba(5,41,42,0.08)' : 'none',
                  flex: isMobile ? 1 : undefined,
                  textAlign: 'center',
                }}
              >
                {presetLabel(id)}
              </button>
            );
          })}
        </div>

        <div
          className="flex items-center"
          style={{
            marginLeft: isMobile ? 0 : 'auto',
            width: isMobile ? '100%' : undefined,
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <FilterField label={language?.['From'] ?? 'From'} icon={<IconCalendar />} fluid={isMobile}>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="bg-transparent outline-none"
              style={{
                fontSize: '13px',
                fontWeight: 500,
                color: TEAL_DARK,
                minWidth: '128px',
                flex: isMobile ? 1 : undefined,
                width: isMobile ? '100%' : undefined,
              }}
            />
          </FilterField>
          <FilterField label={language?.['To'] ?? 'To'} icon={<IconCalendar />} fluid={isMobile}>
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="bg-transparent outline-none"
              style={{
                fontSize: '13px',
                fontWeight: 500,
                color: TEAL_DARK,
                minWidth: '128px',
                flex: isMobile ? 1 : undefined,
                width: isMobile ? '100%' : undefined,
              }}
            />
          </FilterField>
          <FilterField label={language?.['Location'] ?? 'Location'} icon={<IconPin />} fluid={isMobile}>
            <select
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              className="bg-transparent outline-none"
              style={{
                fontSize: '13px',
                fontWeight: 500,
                color: TEAL_DARK,
                minWidth: '128px',
                flex: isMobile ? 1 : undefined,
                width: isMobile ? '100%' : undefined,
              }}
            >
              <option value="">{language?.['All locations'] ?? 'All locations'}</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </FilterField>
        </div>
      </div>
    </div>
  );
};

const RevenueHero = ({
  language,
  loading,
  revenue,
  period,
  compareLabel,
  daily,
}: {
  language: any;
  loading: boolean;
  revenue: Kpi;
  period: { from: string; to: string };
  compareLabel: string;
  daily: DailyPoint[];
}) => {
  const sparkValues = daily.map((point) => point.revenue);
  return (
    <div
      className="mt-5 rounded-2xl"
      style={{
        background: `linear-gradient(135deg, ${TEAL_DARK} 0%, #0B4D4F 55%, ${TEAL} 100%)`,
        color: '#fff',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 18px 48px rgba(5,41,42,0.18)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '420px',
          height: '100%',
          background: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.18), transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <div className="flex flex-wrap items-end justify-between gap-6" style={{ position: 'relative' }}>
        <div>
          <p style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.7)' }}>
            {language?.['Total Revenue'] ?? 'Total Revenue'}
          </p>
          {loading ? (
            <Skeleton height="44px" width="220px" tone="dark" />
          ) : (
            <p style={{ fontSize: '40px', fontWeight: 700, marginTop: '8px', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(revenue.value)}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3" style={{ marginTop: '12px' }}>
            <DeltaPill deltaPct={revenue.deltaPct} dark />
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>
              {language?.['vs'] ?? 'vs'} {formatMoney(revenue.prev)}
              {compareLabel && (
                <span style={{ color: 'rgba(255,255,255,0.55)' }}> · {compareLabel}</span>
              )}
            </span>
          </div>
        </div>

        <div style={{ width: '220px', height: '90px', position: 'relative' }}>
          <Sparkline values={sparkValues} stroke="#A7F3D0" fill="rgba(167,243,208,0.18)" />
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', textAlign: 'right', marginTop: '4px' }}>
            {formatRange(period.from, period.to)}
          </p>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({
  title,
  value,
  previous,
  previousLabel,
  deltaPct,
  invertDelta,
  sub,
  icon,
  accent,
  series,
  loading,
}: {
  title: string;
  value: string;
  previous: string;
  previousLabel: string;
  deltaPct: number;
  invertDelta?: boolean;
  sub?: string;
  icon: React.ReactNode;
  accent: string;
  series?: number[];
  loading: boolean;
}) => (
  <div
    className="rounded-2xl transition"
    style={{
      backgroundColor: '#fff',
      border: `1px solid ${BORDER}`,
      padding: '18px',
      boxShadow: CARD_SHADOW,
      minHeight: '156px',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div className="flex items-start justify-between">
      <div
        className="flex items-center justify-center rounded-xl"
        style={{
          width: '36px',
          height: '36px',
          backgroundColor: `${accent}14`,
          color: accent,
        }}
      >
        {icon}
      </div>
      <DeltaPill deltaPct={deltaPct} invert={invertDelta} />
    </div>

    {loading ? (
      <Skeleton height="40px" width="80%" />
    ) : (
      <>
        <p style={{ fontSize: '13px', color: MUTED, fontWeight: 500, marginTop: '14px' }}>{title}</p>
        <p
          style={{
            fontSize: '24px',
            fontWeight: 700,
            marginTop: '4px',
            letterSpacing: '-0.01em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </p>
        {sub && (
          <p style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{sub}</p>
        )}
        <div className="flex items-end justify-between" style={{ marginTop: '12px' }}>
          <p style={{ fontSize: '11px', color: '#94A3B8' }}>
            {previousLabel} <span style={{ color: MUTED, fontWeight: 600 }}>{previous}</span>
          </p>
          {series && series.length > 1 && (
            <div style={{ width: '88px', height: '28px' }}>
              <Sparkline values={series} stroke={accent} fill={`${accent}1a`} />
            </div>
          )}
        </div>
      </>
    )}
  </div>
);

const DeltaPill = ({
  deltaPct,
  invert,
  dark,
}: {
  deltaPct: number;
  invert?: boolean;
  dark?: boolean;
}) => {
  const positive = deltaPct >= 0;
  const isGood = invert ? !positive : positive;
  const baseBg = isGood ? POSITIVE_BG : NEGATIVE_BG;
  const baseText = isGood ? POSITIVE_TEXT : NEGATIVE_TEXT;
  const bg = dark ? (isGood ? 'rgba(16,185,129,0.18)' : 'rgba(248,113,113,0.22)') : baseBg;
  const color = dark ? (isGood ? '#A7F3D0' : '#FECACA') : baseText;
  return (
    <span
      className="inline-flex items-center rounded-full"
      style={{
        backgroundColor: bg,
        color,
        fontSize: '12px',
        fontWeight: 700,
        padding: '5px 12px',
        gap: '6px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        {positive ? (
          <path d="M5 1.5l3 3.5H6.2v3H3.8v-3H2L5 1.5z" fill="currentColor" />
        ) : (
          <path d="M5 8.5l-3-3.5h1.8v-3h2.4v3H8L5 8.5z" fill="currentColor" />
        )}
      </svg>
      {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
};

const DailyTrendChart = ({
  language,
  loading,
  daily,
  isMobile,
}: {
  language: any;
  loading: boolean;
  daily: DailyPoint[];
  isMobile?: boolean;
}) => {
  const width = 720;
  const height = 280;
  const padX = 36;
  const padY = 24;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const maxValue = Math.max(
    1,
    ...daily.map((point) => Math.max(point.revenue, point.refunds))
  );
  const points = daily.length;

  const xFor = (index: number) => {
    if (points <= 1) return padX + innerW / 2;
    return padX + (index / (points - 1)) * innerW;
  };
  const yFor = (value: number) => padY + innerH - (value / maxValue) * innerH;

  const buildPath = (key: 'revenue' | 'refunds', closed: boolean) => {
    if (!daily.length) return '';
    const cmds = daily.map((point, idx) => {
      const x = xFor(idx);
      const y = yFor(point[key]);
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    });
    if (closed) {
      cmds.push(`L${xFor(daily.length - 1).toFixed(2)} ${(padY + innerH).toFixed(2)}`);
      cmds.push(`L${xFor(0).toFixed(2)} ${(padY + innerH).toFixed(2)} Z`);
    }
    return cmds.join(' ');
  };

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => (maxValue * (yTicks - i)) / yTicks);
  const xLabels = daily.length
    ? Array.from({ length: Math.min(7, daily.length) }, (_, i) => {
        const idx = Math.round((i * (daily.length - 1)) / (Math.min(7, daily.length) - 1 || 1));
        return { idx, date: daily[idx].date };
      })
    : [];

  const totalRevenue = daily.reduce((sum, point) => sum + Number(point.revenue || 0), 0);
  const totalRefunds = daily.reduce((sum, point) => sum + Number(point.refunds || 0), 0);

  return (
    <section
      className="rounded-2xl"
      style={{
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        padding: '20px',
        boxShadow: CARD_SHADOW,
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p style={{ fontSize: '16px', fontWeight: 700 }}>
            {language?.['Daily trend'] ?? 'Daily trend'}
          </p>
          <p style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
            {language?.['Revenue and refunds per day'] ?? 'Revenue and refunds per day'}
          </p>
        </div>
        <div className="flex items-center gap-4" style={{ fontSize: '12px', color: MUTED }}>
          <LegendDot color={TEAL} label={`${language?.['Revenue'] ?? 'Revenue'} · ${formatMoney(totalRevenue)}`} />
          <LegendDot color="#EF4444" label={`${language?.['Refunds'] ?? 'Refunds'} · ${formatMoney(totalRefunds)}`} dashed />
        </div>
      </div>

      {loading ? (
        <Skeleton height="280px" />
      ) : daily.length === 0 ? (
        <EmptyState label={language?.['No data for this range'] ?? 'No data for this range'} />
      ) : (
        <div style={{ marginTop: '16px', overflowX: 'auto' }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: '100%',
              minWidth: isMobile ? 0 : '480px',
              height: isMobile ? 'auto' : '280px',
              maxHeight: '320px',
              display: 'block',
            }}
          >
            <defs>
              <linearGradient id="revArea" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={TEAL} stopOpacity="0.28" />
                <stop offset="100%" stopColor={TEAL} stopOpacity="0" />
              </linearGradient>
            </defs>

            {tickValues.map((value, idx) => {
              const y = padY + (idx / yTicks) * innerH;
              return (
                <g key={`grid-${idx}`}>
                  <line x1={padX} x2={width - padX} y1={y} y2={y} stroke={BORDER} strokeDasharray="3 4" />
                  <text x={padX - 8} y={y + 4} fontSize="10" fill={MUTED} textAnchor="end">
                    {formatAxisMoney(value)}
                  </text>
                </g>
              );
            })}

            <path d={buildPath('revenue', true)} fill="url(#revArea)" />
            <path
              d={buildPath('revenue', false)}
              fill="none"
              stroke={TEAL}
              strokeWidth="2.4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <path
              d={buildPath('refunds', false)}
              fill="none"
              stroke="#EF4444"
              strokeWidth="2"
              strokeDasharray="5 4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {daily.map((point, idx) => (
              <g key={`pt-${point.date}`}>
                <circle cx={xFor(idx)} cy={yFor(point.revenue)} r="3.5" fill="#fff" stroke={TEAL} strokeWidth="2" />
                <title>{`${formatShortDate(point.date)} · ${formatMoney(point.revenue)} revenue · ${formatMoney(point.refunds)} refunds`}</title>
              </g>
            ))}

            {xLabels.map(({ idx, date }) => (
              <text
                key={`xlbl-${date}`}
                x={xFor(idx)}
                y={height - 4}
                fontSize="10"
                fill={MUTED}
                textAnchor="middle"
              >
                {formatShortDate(date)}
              </text>
            ))}
          </svg>
        </div>
      )}
    </section>
  );
};

const TenderSplit = ({
  language,
  loading,
  tenders,
  totalTender,
}: {
  language: any;
  loading: boolean;
  tenders: Tender[];
  totalTender: number;
}) => {
  const size = 180;
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const arcs = tenders.map((tender, index) => {
    const length = (tender.sharePct / 100) * circumference;
    const dasharray = `${length} ${circumference - length}`;
    const dashoffset = -offset;
    offset += length;
    return {
      key: tender.deviceTenderId ?? `${tender.tenderName}-${index}`,
      color: TENDER_COLORS[index % TENDER_COLORS.length],
      dasharray,
      dashoffset,
      tender,
    };
  });

  return (
    <section
      className="rounded-2xl"
      style={{
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        padding: '20px',
        boxShadow: CARD_SHADOW,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p style={{ fontSize: '16px', fontWeight: 700 }}>
            {language?.['Tender split'] ?? 'Tender split'}
          </p>
          <p style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
            {language?.['Payments by tender'] ?? 'Payments by tender'}
          </p>
        </div>
      </div>

      {loading ? (
        <Skeleton height="280px" />
      ) : tenders.length === 0 ? (
        <EmptyState label={language?.['No tender data'] ?? 'No tender data'} />
      ) : (
        <>
          <div className="flex items-center justify-center" style={{ marginTop: '12px' }}>
            <div style={{ width: size, height: size, position: 'relative' }}>
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={BG}
                  strokeWidth={stroke}
                />
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                  {arcs.map((arc) => (
                    <circle
                      key={arc.key}
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke={arc.color}
                      strokeWidth={stroke}
                      strokeDasharray={arc.dasharray}
                      strokeDashoffset={arc.dashoffset}
                      strokeLinecap="butt"
                    />
                  ))}
                </g>
              </svg>
              <div
                className="absolute inset-0 flex flex-col items-center justify-center text-center"
                style={{ pointerEvents: 'none' }}
              >
                <p style={{ fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {language?.['Total'] ?? 'Total'}
                </p>
                <p style={{ fontSize: '18px', fontWeight: 700, marginTop: '2px' }}>{formatMoney(totalTender)}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3" style={{ marginTop: '16px' }}>
            {tenders.map((tender, index) => (
              <div key={tender.deviceTenderId ?? `${tender.tenderName}-${index}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '999px',
                        backgroundColor: TENDER_COLORS[index % TENDER_COLORS.length],
                        display: 'inline-block',
                      }}
                    />
                    <p style={{ fontSize: '13px', fontWeight: 600 }}>{tender.tenderName}</p>
                  </div>
                  <p style={{ fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(tender.total)}
                  </p>
                </div>
                <div
                  style={{
                    marginTop: '6px',
                    width: '100%',
                    height: '6px',
                    borderRadius: '999px',
                    backgroundColor: BG,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, tender.sharePct)}%`,
                      height: '100%',
                      backgroundColor: TENDER_COLORS[index % TENDER_COLORS.length],
                      borderRadius: '999px',
                    }}
                  />
                </div>
                <p style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>
                  {tender.sharePct.toFixed(1)}%
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const Sparkline = ({ values, stroke, fill }: { values: number[]; stroke: string; fill?: string }) => {
  if (!values || values.length < 2) {
    return (
      <svg width="100%" height="100%" viewBox="0 0 100 30" preserveAspectRatio="none">
        <line x1="0" x2="100" y1="20" y2="20" stroke={stroke} strokeOpacity="0.35" strokeWidth="1.5" />
      </svg>
    );
  }
  const w = 100;
  const h = 30;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values.map((value, idx) => {
    const x = (idx / (values.length - 1)) * w;
    const y = h - ((value - min) / range) * h;
    return [x, y] as const;
  });
  const lineD = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
  const areaD = `${lineD} L${w} ${h} L0 ${h} Z`;
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {fill && <path d={areaD} fill={fill} />}
      <path d={lineD} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const Skeleton = ({
  height,
  width,
  tone = 'light',
}: {
  height: string;
  width?: string;
  tone?: 'light' | 'dark';
}) => (
  <div
    className="animate-pulse rounded-xl"
    style={{
      height,
      width: width ?? '100%',
      backgroundColor: tone === 'dark' ? 'rgba(255,255,255,0.12)' : '#EEF3F4',
      marginTop: '12px',
    }}
  />
);

const EmptyState = ({ label }: { label: string }) => (
  <div
    className="flex flex-col items-center justify-center rounded-xl"
    style={{
      marginTop: '16px',
      padding: '32px 16px',
      color: MUTED,
      backgroundColor: BG,
      border: `1px dashed ${BORDER}`,
    }}
  >
    <IconChart />
    <p style={{ fontSize: '13px', marginTop: '8px' }}>{label}</p>
  </div>
);

const LegendDot = ({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) => (
  <span className="inline-flex items-center gap-2">
    <span
      style={{
        width: '20px',
        height: '0',
        borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
        display: 'inline-block',
      }}
    />
    <span style={{ fontSize: '12px', color: MUTED, fontWeight: 500 }}>{label}</span>
  </span>
);

const formatAxisMoney = (value: number) => {
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  return `${value.toFixed(0)}`;
};

const IconRefresh = ({ spinning }: { spinning?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: spinning ? 'spin 1s linear infinite' : undefined }}
  >
    <path d="M21 12a9 9 0 1 1-3-6.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M21 4v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
);
const IconPin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12z" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const IconReceipt = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconTag = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="8" cy="8" r="1.6" fill="currentColor" />
  </svg>
);
const IconRefund = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 12a9 9 0 1 0 3-6.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M3 4v5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconSparkle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);
const IconCoin = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7.5v9M9.6 10.2c0-1 1.1-1.6 2.4-1.6s2.4.6 2.4 1.6-1.1 1.6-2.4 1.6-2.4.6-2.4 1.6 1.1 1.6 2.4 1.6 2.4-.6 2.4-1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconBag = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M5 8h14l-1 12H6L5 8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 8a3 3 0 1 1 6 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconActivity = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 12h4l3-8 4 16 3-8h4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);
const IconAlert = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7v6M12 16.5v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconChart = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ color: '#94A3B8' }}>
    <path d="M4 19V5M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M8 15l3-4 3 2 4-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default SalesOverview;
